import {
  useId,
  useState,
  type KeyboardEventHandler,
  type ReactNode,
} from "react";
import {
  IconArrowUp,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconPencil,
  IconX,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";
import { InlineButton } from "./InlineButton";
import { Input } from "./Input";

/** One selectable option. `id` is the stable key the parent tracks. */
export type ChatOption = { id: string; label: string };

/** Optional "N of M" pager shown in the header controls. */
export type ChatOptionsPager = {
  label: string;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
};

type InlineFreeText = {
  kind: "inline";
  /** Collapsed row label (e.g. "Add own topics", "Other"). */
  label: string;
  /** True when the inline input replaces the row. */
  open: boolean;
  /** Tapping the collapsed row opens the input. */
  onOpen: () => void;
  /** Input placeholder while open. */
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Submit the typed text (parent resolves it). */
  onSubmit: () => void;
  /** Disables send (while resolving, or the input is empty). */
  sendDisabled: boolean;
};

type FieldFreeText = {
  kind: "field";
  /** Row label (e.g. "Other…"); tapping it focuses the input. */
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
};

/**
 * Free-text "add your own" affordance.
 * - `inline`: below the options, tapping the row swaps it for an input *inside
 *   the card* (parent-controlled `open`, so it can close again after resolving
 *   a submitted value). `onSubmit` fires on send; the parent resolves the typed
 *   text and typically appends it as a pre-selected option, then sets `open`
 *   back to false.
 * - `field`: the last row of the list, with its input always shown, for an
 *   answer typed in the reader's own words. It has no send control: the footer
 *   confirm button (`onContinue`) sends, and Enter in the input presses it
 *   while it is enabled.
 */
export type ChatOptionsFreeText = InlineFreeText | FieldFreeText;

/**
 * The free-text part and the footer confirm action go together: a `field` has
 * no send control of its own, the footer button sends, so it cannot go without
 * one.
 */
type FreeTextAndConfirm =
  | {
      /** Inline free-text row (see `ChatOptionsFreeText`). `null` hides it. */
      freeText: InlineFreeText | null;
      /** Footer confirm action (full-width primary button). `null` hides the
       * button entirely (e.g. a bare quick-reply list); otherwise it always
       * renders and `continueDisabled` controls its enabled state. */
      onContinue: (() => void) | null;
    }
  | { freeText: FieldFreeText; onContinue: () => void };

type ChatOptionsProps = {
  /** Header title (e.g. "Select your topics"). `null` hides the title — use it
   * when a preceding message already carries the question. */
  title: string | null;
  /** Single-select renders numbered badges with a trailing check on the chosen
   * row (radio semantics); multiple renders checkbox badges (empty → checked).
   * The parent owns the selection model either way. */
  select: "single" | "multiple";
  /** Options, rendered in order. */
  options: ChatOption[];
  /** Ids currently selected. */
  selectedIds: string[];
  /** Toggle/select an option. `null` shows the options read-only. */
  onSelect: ((id: string) => void) | null;
  /** Footer confirm label (e.g. "Continue"). */
  continueLabel: string;
  /** Greys out the confirm button (e.g. until a selection is made). */
  continueDisabled: boolean;
  /** Header close button. `null` hides it. */
  onClose: (() => void) | null;
  /** Header "N of M" pager. `null` hides it. */
  pager: ChatOptionsPager | null;
  /** Where the card sits: floating in the composer band, or in the transcript's
   * scroll flow. */
  placement: keyof typeof PLACEMENT_SURFACE;
} & FreeTextAndConfirm;

// The band floats glass over the transcript. A transcript card sits in the flow:
// nothing scrolls behind it, so it carries neither the blur nor the shadow.
const PLACEMENT_SURFACE = {
  band: "backdrop-blur-[3px] shadow-[0px_14px_18px_rgba(13,20,18,0.16)]",
  transcript: "",
} as const satisfies Record<string, string>;

/**
 * Chat option-selection surface — the elicitation counterpart to `ChatInput`.
 * A card of options plus an optional free-text row and a confirm button, to
 * let the user answer a scripted question by tapping (or typing their own)
 * rather than swapping to a separate composer. Shown floating in the composer
 * band, or inside the transcript (see `placement`).
 */
function ChatOptions({
  title,
  select,
  options,
  selectedIds,
  onSelect,
  freeText,
  onContinue,
  continueLabel,
  continueDisabled,
  onClose,
  pager,
  placement,
}: ChatOptionsProps): React.JSX.Element {
  const selected = new Set(selectedIds);
  const showHeader = title !== null || pager !== null || onClose !== null;
  // What Enter runs in a `field`: the footer button, while it is there and enabled.
  const enabledContinue =
    onContinue !== null && !continueDisabled ? onContinue : null;

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-6 rounded-2xl p-4",
        "border border-solid border-ds-slate-100",
        "bg-[rgba(253,253,253,0.9)]",
        PLACEMENT_SURFACE[placement]
      )}
    >
      <div className="flex w-full flex-col gap-3">
        {showHeader && (
          <div className="flex w-full items-center gap-2.5">
            <p className="min-w-0 flex-1 font-['DM_Sans',sans-serif] font-semibold text-[17px]/[23px] text-ds-slate-900">
              {title}
            </p>
            {(pager !== null || onClose !== null) && (
              <div className="flex shrink-0 items-center gap-2">
                {pager !== null && (
                  <div className="flex items-center gap-1.5 rounded-full bg-[rgba(11,17,16,0.14)] px-2 py-1">
                    <button
                      type="button"
                      aria-label="Previous"
                      disabled={pager.onPrev === null}
                      onClick={pager.onPrev === null ? undefined : pager.onPrev}
                      className="cursor-pointer text-ds-slate-800 disabled:opacity-40"
                    >
                      <IconChevronLeft size={12} stroke={2} />
                    </button>
                    <span className="font-['DM_Sans',sans-serif] text-[12px] text-ds-slate-800">
                      {pager.label}
                    </span>
                    <button
                      type="button"
                      aria-label="Next"
                      disabled={pager.onNext === null}
                      onClick={pager.onNext === null ? undefined : pager.onNext}
                      className="cursor-pointer text-ds-slate-800 disabled:opacity-40"
                    >
                      <IconChevronRight size={12} stroke={2} />
                    </button>
                  </div>
                )}
                {onClose !== null && (
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[rgba(11,17,16,0.14)] text-ds-slate-800"
                  >
                    <IconX size={15} stroke={2.5} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex w-full flex-col">
          <OptionsList placement={placement}>
            {options.map((option, index) => (
              <OptionRow
                key={option.id}
                selected={selected.has(option.id)}
                onClick={
                  onSelect === null
                    ? null
                    : () => {
                        onSelect(option.id);
                      }
                }
                label={option.label}
                leading={
                  select === "multiple"
                    ? { kind: "checkbox" }
                    : { kind: "number", index: index + 1 }
                }
              />
            ))}
          </OptionsList>

          {freeText !== null && (
            <FreeText freeText={freeText} onEnter={enabledContinue} />
          )}
        </div>
      </div>

      {onContinue !== null && (
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={continueDisabled}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * The options column. In the transcript it stays a plain column: a scroller
 * nested in the page would catch the swipes that start on the card and, once
 * at its end, refuse to hand them on, so the page stops scrolling.
 */
function OptionsList({
  placement,
  children,
}: {
  placement: ChatOptionsProps["placement"];
  children: ReactNode;
}): React.JSX.Element {
  switch (placement) {
    case "band":
      return <OptionsScrollArea>{children}</OptionsScrollArea>;
    case "transcript":
      return <div className="flex w-full flex-col gap-2">{children}</div>;
    default:
      throw new Error(placement satisfies never);
  }
}

/**
 * Scrollable options viewport, for the band only. The card is anchored to the
 * bottom of the composer band, so a long option list would otherwise grow past
 * the top of the screen and become unreachable (header and confirm button stay
 * put, the list scrolls inside). Fog gradients on both edges signal the hidden
 * options, each shown only while there is content to scroll in that direction.
 */
function OptionsScrollArea({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [edges, setEdges] = useState({ top: false, bottom: false });

  function measure(el: HTMLDivElement): void {
    const overflow = el.scrollHeight - el.clientHeight;
    const top = el.scrollTop > 1;
    const bottom = overflow > 1 && el.scrollTop < overflow - 1;
    setEdges((prev) =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
    );
  }

  return (
    <div className="relative w-full">
      <div
        ref={(el) => {
          if (el !== null) measure(el);
        }}
        onScroll={(e) => {
          measure(e.currentTarget);
        }}
        className="flex max-h-[40dvh] w-full flex-col gap-2 overflow-y-auto overscroll-contain"
      >
        {children}
      </div>
      <ScrollFog edge="top" visible={edges.top} />
      <ScrollFog edge="bottom" visible={edges.bottom} />
    </div>
  );
}

/** Edge gradient over the scrollable options, matching the card's glass fill. */
function ScrollFog({
  edge,
  visible,
}: {
  edge: "top" | "bottom";
  visible: boolean;
}): React.JSX.Element {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-0 h-8 transition-opacity duration-150 ease-out",
        edge === "top"
          ? "top-0 bg-[linear-gradient(to_bottom,rgba(253,253,253,0.95),rgba(253,253,253,0))]"
          : "bottom-0 bg-[linear-gradient(to_top,rgba(253,253,253,0.95),rgba(253,253,253,0))]",
        visible ? "opacity-100" : "opacity-0"
      )}
    />
  );
}

// What every row of the list shares: its shape, and its resting grey.
const ROW_SHAPE = "rounded-xl border border-solid px-3 py-2.5";
const ROW_RESTING = "bg-[rgba(11,17,16,0.04)] border-transparent";

/** The free-text part of the card, placed by its variant. */
function FreeText({
  freeText,
  onEnter,
}: {
  freeText: ChatOptionsFreeText;
  // What Enter runs in a `field`; `null` when there is nothing to press.
  onEnter: (() => void) | null;
}): React.JSX.Element {
  switch (freeText.kind) {
    case "inline":
      return <InlineRow inline={freeText} />;
    case "field":
      return <FieldRow field={freeText} onEnter={onEnter} />;
    default:
      throw new Error(freeText satisfies never);
  }
}

/** Below the options, behind a divider: a pencil row that opens into an input. */
function InlineRow({ inline }: { inline: InlineFreeText }): React.JSX.Element {
  return (
    <div className="mt-3 flex w-full flex-col gap-2.5">
      <div className="h-px w-full bg-ds-slate-100" />
      {inline.open ? (
        <Input
          variant="ghost"
          autoFocus
          className="bg-[rgba(11,17,16,0.04)]"
          value={inline.value}
          onChange={(e) => {
            inline.onChange(e.target.value);
          }}
          onKeyDown={submitOnEnter(() => {
            if (!inline.sendDisabled) inline.onSubmit();
          })}
          placeholder={inline.placeholder}
          trailing={
            <InlineButton
              state={inline.sendDisabled ? "disabled" : "active"}
              aria-label="Send"
              onClick={inline.onSubmit}
            >
              <IconArrowUp />
            </InlineButton>
          }
        />
      ) : (
        <OptionRow
          selected={false}
          onClick={inline.onOpen}
          label={inline.label}
          leading={{ kind: "pencil" }}
        />
      )}
    </div>
  );
}

/**
 * The last row of the list: pencil badge and label, then the input. The label
 * is the input's `<label>`, so tapping it focuses the input. No autofocus: the
 * keyboard must not open when the card appears.
 */
function FieldRow({
  field,
  onEnter,
}: {
  field: FieldFreeText;
  onEnter: (() => void) | null;
}): React.JSX.Element {
  const inputId = useId();
  return (
    <div
      className={cn("mt-2 flex w-full flex-col gap-2", ROW_SHAPE, ROW_RESTING)}
    >
      <label
        htmlFor={inputId}
        className="flex cursor-pointer items-center gap-3"
      >
        <OptionBadge leading={{ kind: "pencil" }} selected={false} />
        <OptionLabel className="text-ds-mint-900">{field.label}</OptionLabel>
      </label>
      <Input
        id={inputId}
        className="border-ds-slate-100 bg-white shadow-none"
        value={field.value}
        onChange={(e) => {
          field.onChange(e.target.value);
        }}
        onKeyDown={submitOnEnter(() => {
          if (onEnter !== null) onEnter();
        })}
        placeholder={field.placeholder}
      />
    </div>
  );
}

/** Enter submits, except while an IME composition is open: Enter then picks a candidate. */
function submitOnEnter(
  submit: () => void
): KeyboardEventHandler<HTMLInputElement> {
  return (e) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };
}

/** Leading affordance of an option row; drives the badge's shape and colors. */
type OptionLeading =
  { kind: "number"; index: number } | { kind: "checkbox" } | { kind: "pencil" };

/** 28px square that holds the number, check, or pencil glyph. */
function SquareBadge({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-lg",
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * Leading badge, coloured by role (DS 5.0): a selected number/checkbox fills
 * mint-800 with a white glyph; unselected sits on neutral grey (the number keeps
 * a moss-600 digit, the checkbox is an empty outlined square); the pencil "add
 * your own" badge is always neutral — never an accent.
 */
function OptionBadge({
  leading,
  selected,
}: {
  leading: OptionLeading;
  selected: boolean;
}): React.JSX.Element {
  switch (leading.kind) {
    case "number":
      if (selected)
        return (
          <SquareBadge className="bg-ds-primary text-ds-on-primary">
            <span className="font-['DM_Sans',sans-serif] text-[14px] font-semibold">
              {leading.index}
            </span>
          </SquareBadge>
        );
      return (
        <SquareBadge className="bg-[rgba(11,17,16,0.06)] text-ds-moss-600">
          <span className="font-['DM_Sans',sans-serif] text-[14px] font-semibold">
            {leading.index}
          </span>
        </SquareBadge>
      );
    case "checkbox":
      if (selected)
        return (
          <SquareBadge className="bg-ds-primary text-ds-on-primary">
            <IconCheck size={16} stroke={2} />
          </SquareBadge>
        );
      return (
        <span className="size-7 shrink-0 rounded-lg border-[1.5px] border-solid border-[rgba(11,17,16,0.14)]" />
      );
    case "pencil":
      return (
        <SquareBadge className="bg-[rgba(11,17,16,0.06)] text-ds-mint-900">
          <IconPencil size={16} stroke={2} />
        </SquareBadge>
      );
    default:
      throw new Error(leading satisfies never);
  }
}

function OptionRow({
  leading,
  label,
  selected,
  onClick,
}: {
  leading: OptionLeading;
  label: string;
  selected: boolean;
  // Null for a read-only row.
  onClick: (() => void) | null;
}): React.JSX.Element {
  // DS 5.0 selection colors: moss-100 surface + moss-300 liseré + mint-800
  // badge/label accent.
  const container = selected
    ? "bg-ds-moss-100 border-ds-border-selected"
    : ROW_RESTING;

  const labelColor =
    selected && leading.kind !== "pencil"
      ? "text-ds-primary"
      : "text-ds-mint-900";

  return (
    <button
      type="button"
      onClick={onClick === null ? undefined : onClick}
      disabled={onClick === null}
      className={cn(
        "flex w-full items-center gap-3 text-left",
        ROW_SHAPE,
        onClick === null ? "cursor-default" : "cursor-pointer",
        "transition-colors duration-150 ease-out",
        container
      )}
    >
      <OptionBadge leading={leading} selected={selected} />
      <OptionLabel className={labelColor}>{label}</OptionLabel>
      {leading.kind === "number" && selected && (
        <IconCheck size={18} stroke={2} className="shrink-0 text-ds-primary" />
      )}
    </button>
  );
}

function OptionLabel({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "min-w-0 flex-1 font-['DM_Sans',sans-serif] font-medium text-[15px]/5 tracking-[-0.15px]",
        className
      )}
    >
      {children}
    </span>
  );
}

export { ChatOptions, type ChatOptionsProps };
